import type { LucideIcon } from "lucide-react";

import { Link } from "@tanstack/react-router";
import { twMerge } from "tailwind-merge";

import { Badge } from "@/components/badge";

export type DashboardCardConfig = {
  displayOrder: number;
  featured?: boolean;
  icon: LucideIcon;
  iconBgClass: string;
  iconClass: string;
  label: string;
  search?: Record<string, string>;
  to: string;
};

type DashboardCardProps = {
  className?: string;
  config: DashboardCardConfig;
  count: number;
  jumbo?: boolean;
};

export function DashboardCard({
  className,
  config,
  count,
  jumbo = false,
}: DashboardCardProps) {
  const {
    featured,
    icon: Icon,
    iconBgClass,
    iconClass,
    label,
    search,
    to,
  } = config;

  return (
    <Link
      className={twMerge(
        "flex flex-col justify-between bg-bg-primary border border-border-primary rounded-3xl shadow-sm p-6 flex-1 cursor-pointer transition-shadow hover:shadow-md",
        className,
      )}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      search={search as any}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      to={to as any}
    >
      <div className="flex items-start justify-between">
        <div className={twMerge("rounded-2xl p-3 shrink-0", iconBgClass)}>
          <Icon className={twMerge("size-5", iconClass)} />
        </div>
        {featured && (
          <Badge isRounded size="medium" variant="warning">
            High priority
          </Badge>
        )}
      </div>
      <div className="flex flex-col mt-4">
        <span
          className={twMerge(
            "font-medium text-text-tertiary",
            jumbo ? "text-lg" : "text-sm",
          )}
        >
          {label}
        </span>
        <span
          className={twMerge(
            "font-bold tracking-tight",
            jumbo ? "text-[60px] leading-[72px]" : "text-[36px] leading-[44px]",
            featured
              ? "text-utility-warning-600"
              : count === 0
                ? "text-text-secondary"
                : "text-text-primary",
          )}
        >
          {count.toLocaleString()}
        </span>
      </div>
    </Link>
  );
}
